/**
 * Extracted from AdminRestaurants.jsx — the per-row shops listing
 * table with health-chip, owner column, vertical chips, and the
 * approve / reject / toggle / edit / delete action row.
 *
 * Pure presentational component — every handler is passed in from
 * the parent so we don't accidentally fork state.
 *
 * Responsive strategy:
 *   • sm+ (≥640px): classic full table (unchanged for admins on
 *     desktop / tablet).
 *   • <sm (mobile): stacked "card" view showing the most useful
 *     fields (name, owner, vertical, zone, status, health) and the
 *     same action row. Horizontal scroll is a poor mobile UX, so we
 *     switch entire layout to a vertical card grid instead.
 */
import { Pencil, Trash2, Power, Check, XCircle } from "lucide-react";

const Th = ({ children, className = "" }) => (
    <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
    <td className={`px-4 py-3 ${className}`} {...rest}>{children}</td>
);

// Shared status / health helpers so the table + card views stay in sync.
const statusChipClass = (status) =>
    status === "ACTIVE" ? "bg-emerald-100 text-emerald-700"
    : status === "PENDING_APPROVAL" ? "bg-amber-100 text-amber-800"
    : status === "REJECTED" ? "bg-red-100 text-red-700"
    : "bg-muted text-muted-foreground";
const statusLabel = (status) => (status === "PENDING_APPROVAL" ? "PENDING" : status);
const healthDotClass = (level) =>
    level === "red" ? "bg-red-500" : level === "amber" ? "bg-amber-500" : "bg-emerald-500";

function ActionRow({ r, prefix = "", onEdit, onDelete, onToggleStatus, onApprove, onReject }) {
    const tid = (name) => `rest-${name}${prefix}-${r.id}`;
    // iter146 — was `p-2` (~30×30px effective button) around a 14px
    // icon — under Apple HIG's 44×44 touch-target minimum and, per
    // user report, hard to even see at narrower table widths. Reuses
    // the project's own `.touch-target` utility (index.css) rather
    // than inventing new sizing; icon bumped 14→16 for legibility.
    return (
        <div className="inline-flex items-center gap-1 flex-shrink-0">
            {r.status === "PENDING_APPROVAL" && (
                <>
                    <button data-testid={tid("approve")} title="Approve shop" onClick={() => onApprove(r)} className="touch-target rounded-lg text-emerald-600 hover:bg-emerald-50"><Check size={16} /></button>
                    <button data-testid={tid("reject")} title="Reject shop" onClick={() => onReject(r)} className="touch-target rounded-lg text-red-600 hover:bg-red-50"><XCircle size={16} /></button>
                </>
            )}
            <button data-testid={tid("toggle")} title={r.status === "ACTIVE" ? "Mark inactive" : "Mark active"} onClick={() => onToggleStatus(r)}
                className={`touch-target rounded-lg ${r.status === "ACTIVE" ? "text-emerald-600 hover:bg-emerald-50" : "text-muted-foreground hover:bg-muted"}`}>
                <Power size={16} />
            </button>
            <button data-testid={tid("edit")} onClick={() => onEdit(r)} className="touch-target rounded-lg hover:bg-muted"><Pencil size={16} /></button>
            <button data-testid={tid("del")} onClick={() => onDelete(r)} className="touch-target rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={16} /></button>
        </div>
    );
}

function ShopCard({ r, zoneById, verticalById, ownersByShopId, onEdit, onDelete, onToggleStatus, onApprove, onReject }) {
    const dq = r.data_quality || { level: "green", issues: [] };
    const zone = zoneById[r.zone_id];
    const list = ownersByShopId[r.id] || [];
    const first = list[0];
    const fullName = first ? (`${first.first_name || ""} ${first.last_name || ""}`.trim() || first.mobile_number) : null;
    const catIds = (r.business_category_ids || []).slice(0, 2);
    const dqTitle = dq.issues.length ? dq.issues.join(" • ") : "All good";
    return (
        <div className="border-t border-border first:border-t-0 px-4 py-3" data-testid={`rest-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{r.name}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground truncate" data-testid={`rest-owner-card-${r.id}`}>
                        {list.length === 0
                            ? <span className="text-amber-600 font-semibold">— no owner</span>
                            : (
                                <>
                                    <span className="font-semibold text-foreground">{fullName}</span>
                                    <span className="font-mono ml-1">· {first.mobile_number}</span>
                                    {list.length > 1 && <span className="ml-1 text-primary font-semibold">+{list.length - 1}</span>}
                                </>
                            )
                        }
                    </div>
                </div>
                <span className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${statusChipClass(r.status)}`}>{statusLabel(r.status)}</span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`rest-verticals-card-${r.id}`}>
                {catIds.map((cid) => (
                    <span key={cid} className="text-[10px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">{verticalById[cid]?.title || verticalById[cid]?.name || cid}</span>
                ))}
                {catIds.length === 0 && (
                    <span className="text-[10px] text-amber-600 font-semibold">no vertical</span>
                )}
                <span data-testid={`rest-zone-card-${r.id}`} className="text-[10px] font-bold rounded-full px-2 py-0.5"
                    style={{}}
                >
                    {zone
                        ? <span className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded-full px-2 py-0.5">{zone.name}</span>
                        : <span className="text-amber-600 font-semibold">no zone</span>
                    }
                </span>
                {r.rating_count > 0 && typeof r.rating === "number" && (
                    <span className="text-[10px] text-muted-foreground">★ {r.rating.toFixed(1)}</span>
                )}
                <span data-testid={`rest-health-card-${r.id}`} title={dqTitle} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                    <span className={`inline-block w-2 h-2 rounded-full ${healthDotClass(dq.level)}`} />
                    {dq.issues.length ? `${dq.issues.length} issue${dq.issues.length > 1 ? "s" : ""}` : "OK"}
                </span>
            </div>

            <div className="mt-3 flex justify-end">
                <ActionRow r={r} prefix="-card" onEdit={onEdit} onDelete={onDelete} onToggleStatus={onToggleStatus} onApprove={onApprove} onReject={onReject} />
            </div>
        </div>
    );
}

export default function ShopsTable({
    filtered, query, zoneById, verticalById, ownersByShopId,
    onEdit, onDelete, onToggleStatus, onApprove, onReject,
}) {
    const empty = filtered.length === 0;
    return (
        <div className="mt-6 bg-card border border-border rounded-2xl overflow-hidden">
            {/* Mobile + tablet portrait (<lg): stacked card view */}
            <div className="lg:hidden">
                {empty ? (
                    <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                        {query ? `No shops match "${query}"` : "No shops yet"}
                    </div>
                ) : (
                    filtered.map((r) => (
                        <ShopCard key={r.id} r={r}
                            zoneById={zoneById} verticalById={verticalById} ownersByShopId={ownersByShopId}
                            onEdit={onEdit} onDelete={onDelete} onToggleStatus={onToggleStatus}
                            onApprove={onApprove} onReject={onReject}
                        />
                    ))
                )}
            </div>

            {/* Desktop (lg+): classic table with horizontal scroll fallback */}
            <div className="hidden lg:block overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-muted/60 text-left">
                        <tr>
                            <Th>Name</Th><Th>Owner</Th><Th>Vertical</Th><Th>Keywords</Th>
                            <Th>Type</Th><Th>Zone</Th><Th>Rating</Th><Th>Status</Th>
                            <Th>Health</Th>
                            {/* iter146 — sticky so Edit/Delete/Toggle stay reachable
                                without scrolling all the way right on narrower
                                desktop/laptop widths (user-reported: actions
                                effectively hidden past the visible edge). */}
                            <Th className="text-right sticky right-0 bg-muted/60 border-l border-border">Action</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r) => {
                            const dq = r.data_quality || { level: "green", issues: [] };
                            const dqTitle = dq.issues.length ? dq.issues.join(" • ") : "All good";
                            const zone = zoneById[r.zone_id];
                            const list = ownersByShopId[r.id] || [];
                            const first = list[0];
                            const fullName = first ? (`${first.first_name || ""} ${first.last_name || ""}`.trim() || first.mobile_number) : null;
                            return (
                                <tr key={r.id} className="border-t border-border" data-testid={`rest-row-${r.id}`}>
                                    <Td><span className="font-semibold">{r.name}</span></Td>
                                    <Td data-testid={`rest-owner-${r.id}`}>
                                        {list.length === 0
                                            ? <span className="text-[11px] text-amber-600 font-semibold">— none</span>
                                            : (
                                                <div className="leading-tight" title={list.length > 1 ? `+${list.length - 1} more` : ""}>
                                                    <div className="text-xs font-semibold truncate max-w-[120px]">{fullName}</div>
                                                    <div className="text-[10px] text-muted-foreground font-mono">{first.mobile_number}</div>
                                                    {list.length > 1 && (
                                                        <div className="text-[10px] text-primary font-semibold">+{list.length - 1} more</div>
                                                    )}
                                                </div>
                                            )
                                        }
                                    </Td>
                                    <Td data-testid={`rest-verticals-${r.id}`}>
                                        <div className="flex flex-wrap gap-1">
                                            {(r.business_category_ids || []).slice(0, 2).map((cid) => (
                                                <span key={cid} className="text-[10px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">{verticalById[cid]?.title || verticalById[cid]?.name || cid}</span>
                                            ))}
                                            {(!r.business_category_ids || r.business_category_ids.length === 0) && (
                                                <span className="text-[11px] text-amber-600 font-semibold">— none</span>
                                            )}
                                        </div>
                                    </Td>
                                    <Td className="text-muted-foreground">{(r.cuisines || []).slice(0, 2).join(", ")}</Td>
                                    <Td>{r.food_type === "OTHER" ? <span className="text-muted-foreground">—</span> : r.food_type}</Td>
                                    <Td data-testid={`rest-zone-${r.id}`}>
                                        {zone
                                            ? <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-primary/10 text-primary rounded-full px-2 py-0.5">{zone.name}</span>
                                            : <span className="text-[11px] text-amber-600 font-semibold">— unassigned</span>
                                        }
                                    </Td>
                                    <Td>{r.rating_count > 0 && typeof r.rating === "number" ? r.rating.toFixed(1) : <span className="text-muted-foreground">—</span>}</Td>
                                    <Td><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${statusChipClass(r.status)}`}>{statusLabel(r.status)}</span></Td>
                                    <Td>
                                        <span data-testid={`rest-health-${r.id}`} title={dqTitle} className="inline-flex items-center gap-1.5 cursor-help">
                                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${healthDotClass(dq.level)}`} />
                                            <span className="text-[11px] text-muted-foreground">{dq.issues.length ? `${dq.issues.length} issue${dq.issues.length > 1 ? "s" : ""}` : "OK"}</span>
                                        </span>
                                    </Td>
                                    <Td className="text-right sticky right-0 bg-card border-l border-border">
                                        <ActionRow r={r} onEdit={onEdit} onDelete={onDelete} onToggleStatus={onToggleStatus} onApprove={onApprove} onReject={onReject} />
                                    </Td>
                                </tr>
                            );
                        })}
                        {empty && (
                            <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground text-sm">{query ? `No shops match "${query}"` : "No shops yet"}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
